import React, { useId, useState } from 'react';

import { Input } from '@flixbus/honeycomb-react';

import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';
import GroupValidationMessage from './GroupValidationMessage';
import { combineDateTime, splitTimestamp, toDateOnly } from '../utils/incidentTime';
import { isAtOrAfter, isStrictlyBefore } from '../utils/timestampOrder';

import styles from './ReplacementEtdEtaInputs.module.css';

const COPY = CREATE_BREAKDOWN_FLOW_TEXT.replacementEtdEta;
const ERR = CREATE_BREAKDOWN_FLOW_TEXT.errors;

/**
 * "Replacement ETD" / "Replacement ETA" row — two split date+time pairs.
 * Emits `{ fromTimestamp, toTimestamp }` as combined `YYYY-MM-DDTHH:mm` strings so the
 * parent leaf's payload stays shape-stable.
 *
 * Enforces the chain `incident <= ETD < ETA` inside the group:
 *  - ETD pair flags red when `ETD < incident` (baseline).
 *  - ETA pair flags red when `ETA <= ETD` (strict), or `ETA < incident`
 *    when ETD is empty (falls back to baseline).
 *
 * `incidentTimestamp` is optional (some callers may render this outside a
 * flow with a known incident time); when absent the incident-baseline
 * rules are simply skipped.
 *
 * @param {{
 *   value: { fromTimestamp: string, toTimestamp: string },
 *   incidentTimestamp?: string,
 *   onChange: (next: { fromTimestamp: string, toTimestamp: string }) => void,
 * }} props
 */
const ReplacementEtdEtaInputs = ({ value, incidentTimestamp = '', onChange }) => {
	const etdDateId = useId();
	const etdTimeId = useId();
	const etaDateId = useId();
	const etaTimeId = useId();

	const [initialFromDate, initialFromTime] = splitTimestamp(value?.fromTimestamp);
	const [initialToDate, initialToTime] = splitTimestamp(value?.toTimestamp);
	const [fromDate, setFromDate] = useState(initialFromDate);
	const [fromTime, setFromTime] = useState(initialFromTime);
	const [toDate, setToDate] = useState(initialToDate);
	const [toTime, setToTime] = useState(initialToTime);

	const emitChange = (nextFromDate, nextFromTime, nextToDate, nextToTime) => {
		onChange({
			fromTimestamp: combineDateTime(nextFromDate, nextFromTime),
			toTimestamp: combineDateTime(nextToDate, nextToTime),
		});
	};

	const handleFromDate = (event) => {
		const next = event.target.value;
		setFromDate(next);
		emitChange(next, fromTime, toDate, toTime);
	};
	const handleFromTime = (event) => {
		const next = event.target.value;
		setFromTime(next);
		emitChange(fromDate, next, toDate, toTime);
	};
	const handleToDate = (event) => {
		const next = event.target.value;
		setToDate(next);
		emitChange(fromDate, fromTime, next, toTime);
	};
	const handleToTime = (event) => {
		const next = event.target.value;
		setToTime(next);
		emitChange(fromDate, fromTime, toDate, next);
	};

	const currentFrom = combineDateTime(fromDate, fromTime);
	const currentTo = combineDateTime(toDate, toTime);

	// ETD pair is invalid when it precedes the incident. Only checked when
	// both operands exist — an empty ETD or missing incident does not flag.
	const etdBeforeIncident =
		!!currentFrom && !!incidentTimestamp && !isAtOrAfter(currentFrom, incidentTimestamp);

	// ETA pair is invalid when it fails the chain. Strict against ETD when
	// ETD is present; falls back to `>= incident` when ETD is empty.
	const etaBeforeEtd = !!currentFrom && !!currentTo && !isStrictlyBefore(currentFrom, currentTo);
	const etaBeforeIncident =
		!currentFrom &&
		!!currentTo &&
		!!incidentTimestamp &&
		!isAtOrAfter(currentTo, incidentTimestamp);
	const etaInvalid = etaBeforeEtd || etaBeforeIncident;

	const etdGroupError = etdBeforeIncident ? ERR.etdBeforeIncident : undefined;
	const etaGroupError = etaInvalid ? ERR.etaBeforeEtd : undefined;

	// Native date-picker floor: ETD >= incident; ETA >= ETD (or incident).
	const etdMin = toDateOnly(incidentTimestamp);
	const etaMin = toDateOnly(currentFrom) || toDateOnly(incidentTimestamp);

	return (
		<div className={styles.row}>
			<div className={styles.pairWithError}>
				<div className={styles.dateTimePair}>
					<div className={styles.dateField}>
						<Input
							id={etdDateId}
							type="date"
							label={COPY.etdLabel}
							value={fromDate}
							onChange={handleFromDate}
							min={etdMin}
							valid={etdBeforeIncident ? false : undefined}
							data-testid="replacement-etd-date-input"
						/>
					</div>
					<div className={styles.timeField}>
						<Input
							id={etdTimeId}
							type="time"
							aria-label={COPY.etdTimeAriaLabel}
							value={fromTime}
							onChange={handleFromTime}
							valid={etdBeforeIncident ? false : undefined}
							data-testid="replacement-etd-time-input"
						/>
					</div>
				</div>

				<GroupValidationMessage message={etdGroupError} testId="replacement-etd-group-error" />
			</div>

			<div className={styles.pairWithError}>
				<div className={styles.dateTimePair}>
					<div className={styles.dateField}>
						<Input
							id={etaDateId}
							type="date"
							label={COPY.etaLabel}
							value={toDate}
							onChange={handleToDate}
							min={etaMin}
							valid={etaInvalid ? false : undefined}
							data-testid="replacement-eta-date-input"
						/>
					</div>
					<div className={styles.timeField}>
						<Input
							id={etaTimeId}
							type="time"
							aria-label={COPY.etaTimeAriaLabel}
							value={toTime}
							onChange={handleToTime}
							valid={etaInvalid ? false : undefined}
							data-testid="replacement-eta-time-input"
						/>
					</div>
				</div>

				<GroupValidationMessage message={etaGroupError} testId="replacement-eta-group-error" />
			</div>
		</div>
	);
};

export default ReplacementEtdEtaInputs;
